import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Offer } from 'app/services/offers/offer.model';
import { OffersService } from 'app/services/offers/offers.service';
import { map, tap, catchError } from 'rxjs/operators';
import { CreateOffer, DeleteOffer, GetDealOffers, PublishOffer, ToggleOffer, UpdateOffer, ClearOfferStore, ClearOfferError, RemoveFromPortal } from './offer.actions';
import { DealOfferAdded } from '../deal/deal/deal.actions';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';

export class OfferStateModel {
  borrowerId: null | number;
  offers: Offer[];
  loading: boolean;
  error: string;
}

@State<OfferStateModel>({
  name: 'Offer',
  defaults: {
    borrowerId: null,
    offers: [],
    loading: true,
    error: ''
  }
})

@Injectable()

export class OfferState {


  @Selector()
  static error(state: OfferStateModel) {
    return state.error;
  }

  // offers by dealId  - how to use:
  // this.dealOffers$ = this.store.select(OfferState.dealOffers).pipe(map(filterFn => filterFn(this.deal.id)));
  @Selector()
  static dealOffers(state: OfferStateModel) {
    return (dealId: number, dealStage: string) => {
      if (dealStage !== 'funded') {
        return state.offers.filter(o => o.dealId === dealId);
      }
      return state.offers.filter(o => o.dealId === dealId && !o.accepted);
    };
  }

  @Selector()
  static lastDealOffer(state: OfferStateModel) {
    return (dealId: number) => state.offers.find(o => o.dealId === dealId);
  }

  @Selector()
  static highestDealOffer(state: OfferStateModel) {
    return (dealId: number) => {
      const allOffers = state.offers.filter(o => o.dealId === dealId);
      const sortedOffers = allOffers.sort(
        (a,b) => (
          (typeof b.amount === 'string' ? Number(b.amount) : b.amount) - (typeof a.amount === 'string' ? Number(a.amount) : a.amount
        ) ||
          Number(b.created) - Number(a.created)
        )
      );
      return sortedOffers[0];
    };
  }

  @Selector()
  static fundedOffer(state: OfferStateModel) {
    return (dealId: number) => state.offers.filter(o => o.dealId === dealId && o.accepted);
  }

  @Selector()
  static acceptedOfferId(state: OfferStateModel) {
    return (dealId: number) => {
      const acceptedOffer = state.offers.find(o => o.dealId === dealId && o.accepted);
      return acceptedOffer ? acceptedOffer.id : null;
    };
  }

  @Selector()
  static loading(state: OfferStateModel) {
    return state.loading;
  }

  constructor(
    private offersService: OffersService,
    private store: Store
  ) { }

  @Action(GetDealOffers, { cancelUncompleted: true })
  getDealOffers({ getState, patchState }: StateContext<OfferStateModel>, { borrowerId }: GetDealOffers) {
    const prevBorrowerId = getState().borrowerId;

    if (borrowerId === prevBorrowerId) {
      return;
    }

    if (borrowerId !== prevBorrowerId) {
      // reset state
      patchState({
        borrowerId,
        offers: [],
        loading: true
      });
    } else {
      patchState({
        loading: true
      });
    }

    return this.offersService.getDealOffers(borrowerId).pipe(map(offers => {
      offers.forEach(offer => {
        offer.calculatedFields = this.calculatedFields(offer);
      });
      patchState({
        offers: [...getState().offers.filter(offer => !offers.find(offer2 => offer2.id === offer.id)), ...offers],
        loading: false
      });
    }), catchError(() => {
      return of();
    }));
  }

  @Action(DeleteOffer)
  deleteOffer({ getState, patchState }: StateContext<OfferStateModel>, { offerId }: DeleteOffer) {
    return this.offersService.delete(offerId).pipe(tap(() => {
      patchState({ offers: [...getState().offers.filter(offer => offer.id !== offerId)] });
    }));
  }

  @Action(PublishOffer)
  publishOffer({ getState, patchState }: StateContext<OfferStateModel>, { offerId, userId, publishedToBorrower }: PublishOffer) {
    return this.offersService.update(offerId, { publishedByLenderUserId: userId, publishedToBorrower }).pipe(
      catchError(error => {
        patchState({ error: 'Failed to update offer' });
        return of();
      }),
      tap((offer: Offer) => {
        const offersCopy = [...getState().offers];
        const oldOfferIndex = offersCopy.findIndex(o => o.id === offer.id);
        offer.expanded = true;
        offersCopy[oldOfferIndex] = offer;
        patchState({ offers: offersCopy });
      }));
  }

  @Action(RemoveFromPortal)
  removeFromPortal({ getState, patchState }: StateContext<OfferStateModel>, { offerId }: PublishOffer) {
    return this.offersService.update(offerId, { publishedByLenderUserId: null, publishedToBorrower: null }).pipe(
      catchError(error => {
        patchState({ error: 'Failed to update offer' });
        return of();
      }),
      tap((offer: Offer) => {
        const offersCopy = [...getState().offers];
        const oldOfferIndex = offersCopy.findIndex(o => o.id === offer.id);
        offer.expanded = true;
        offersCopy[oldOfferIndex] = offer;
        patchState({ offers: offersCopy });
      }));
  }

  @Action(CreateOffer)
  createOffer({ getState, patchState }: StateContext<OfferStateModel>, { offer }: CreateOffer) {
    return this.offersService.create(offer).pipe(tap(newOffer => {
      newOffer.calculatedFields = this.calculatedFields(newOffer);
      patchState({ offers: [newOffer, ...getState().offers] });
      this.store.dispatch( new DealOfferAdded(newOffer) );
    }));
  }

  @Action(UpdateOffer)
  updateOffer({ getState, patchState }: StateContext<OfferStateModel>, { offerId, updates }: UpdateOffer) {
    return this.offersService.update(offerId, updates).pipe(
      catchError(error => {
        patchState({ error: 'Failed to update offer' });
        return of();
      }),
      tap(offer => {
        const offersCopy = [...getState().offers];
        const oldOfferIndex = offersCopy.findIndex(o => o.id === offer.id);
        offer.expanded = true;
        offersCopy[oldOfferIndex] = offer;
        patchState({ offers: offersCopy });
      }),

    );
  }

  @Action(ToggleOffer)
  toggleOffer({ getState, patchState }: StateContext<OfferStateModel>, { offerId }: ToggleOffer) {
    const offersCopy = [...getState().offers];
    const offerIndex = offersCopy.findIndex(o => o.id === offerId);
    offersCopy[offerIndex].expanded = !offersCopy[offerIndex].expanded;
    patchState({ offers: offersCopy });
  }


  @Action(ClearOfferStore)
  ClearOfferStore({ patchState }: StateContext<OfferStateModel>, { }: ClearOfferStore) {
    patchState({
      borrowerId: null,
      offers: [],
      loading: true
    });
  }


  @Action(ClearOfferError)
  clearOfferError({ patchState }: StateContext<OfferStateModel>, { }: ClearOfferError) {
    patchState({
     error: ''
    });
  }


  calculatedFields(offer: Offer) {
    const cleanedOffer: any = this.cleanOffer(offer);
    const calculated: any = {};

    if (cleanedOffer.amount === undefined || cleanedOffer.amount === 0) {
      return {};
    }

    if (cleanedOffer.paymentFrequency === undefined) {
      return {};
    }

    const paymentsPerMonth = this.monthlyPeriods(cleanedOffer.paymentFrequency);

    if (paymentsPerMonth === null) {
      return {};
    }

    if (cleanedOffer.term === undefined) {
      return {};
    }

    const paymentsPerYear = paymentsPerMonth * 12;

    // If factor is present, base calculations off of it
    if (cleanedOffer.factor !== undefined) {
      calculated.payback = Number(cleanedOffer.amount) * Number(cleanedOffer.factor);
      if (cleanedOffer.numPayments !== undefined) {
        const paymentAmount = calculated.payback / cleanedOffer.numPayments;
        calculated.paymentAmount = paymentAmount.toFixed(2);

      } else {
        const paymentAmount = calculated.payback / (paymentsPerMonth * cleanedOffer.term);
        calculated.paymentAmount = paymentAmount.toFixed(2);
      }

      // If factor is missing, try to use interest rate
    } else if (cleanedOffer.interestRate !== undefined) {
      const ratePerPeriod = Number(cleanedOffer.interestRate) / 100 / paymentsPerYear;
      const numPeriods = paymentsPerMonth * cleanedOffer.term;
      const paymentAmount = ratePerPeriod * Number(cleanedOffer.amount) / (1 - Math.pow(1 + ratePerPeriod, -numPeriods));
      calculated.paymentAmount = paymentAmount.toFixed(2);
      calculated.payback = (paymentAmount * numPeriods).toFixed(2);

      // If factor & interest is missing, try to use payment amount
    } else if (cleanedOffer.paymentAmount !== undefined) {
      if (cleanedOffer.numPayments !== undefined) {
        calculated.payback = Number(cleanedOffer.paymentAmount) * cleanedOffer.numPayments;
        // tslint:disable-next-line: max-line-length
        calculated.interestRate = this.approximateInterestRate(
          Number(cleanedOffer.amount), calculated.payback, cleanedOffer.term, paymentsPerMonth);
        if (calculated.interestRate === null) {
          return {};
        }
      } else {
        const numPeriods = paymentsPerMonth * cleanedOffer.term;
        calculated.payback = numPeriods * Number(cleanedOffer.paymentAmount);
        // tslint:disable-next-line: max-line-length
        calculated.interestRate = this.approximateInterestRate(
          Number(cleanedOffer.amount), calculated.payback, cleanedOffer.term, paymentsPerMonth);
        if (calculated.interestRate === null) {
          return {};
        }
      }
    }

    // Calculate summary stats if possible
    if (calculated.payback !== undefined) {
      const centsOnDollar = calculated.payback / Number(cleanedOffer.amount) - 1;
      calculated.centsOnDollar = centsOnDollar.toFixed(2);
    }
    if (calculated.paymentAmount !== undefined || cleanedOffer.paymentAmount !== undefined) {
      // tslint:disable-next-line: max-line-length
      const dollarsPerThousand = ((calculated.paymentAmount !== undefined) ?
        calculated.paymentAmount : cleanedOffer.paymentAmount) * 1000 / Number(cleanedOffer.amount);
      calculated.dollarsPerThousand = dollarsPerThousand.toFixed(2);
    }

    return calculated;
  }

  private paybackAmount(interestRate: number, amount: number, months: number, periodsPerMonth: number) {
    const periodsPerYear = periodsPerMonth * 12;
    const numPeriods = periodsPerMonth * months;
    const periodInterest = interestRate / periodsPerYear;
    const periodFactor = 1 + periodInterest;
    const firstPeriodInterest = amount * periodInterest;
    const paymentAmount = firstPeriodInterest / (1 - Math.pow(periodFactor, -numPeriods));
    const paybackAmount = paymentAmount * numPeriods;
    return paybackAmount;
  }

  private approximateInterestRate(amount: number, payback: number, months: number, paymentsPerMonth: number, decimalPlaces = 2) {
    if (amount >= payback) {
      return null;
    }

    // Start with a semi-sensible guess at the interest rate (50%)
    let guess = .5;
    const maxIterations = 200;

    // Add 2 extra digits since we're multiplying by 100 at the end
    const precisionDigits = decimalPlaces + 2;
    const precisionMultiplier = Math.pow(10, precisionDigits);

    // Allowable error should be smaller than precision to avoid rounding errors
    const allowableError = 1 / (precisionMultiplier * 10);

    // Exaggerate corrections since we're protected by bounds & averaging anway
    const speedup = 200;

    let closestHighGuess;
    let closestLowGuess;

    for (let i = 0; i < maxIterations; i++) {

      // Its hard to calculate interest rate, but easy to calsulate payback _given_ an interest rate, so
      // we're going to use guess-and-check
      const calculatedPayback = this.paybackAmount(guess, amount, months, paymentsPerMonth);
      const error = calculatedPayback - payback;

      // On the low side, but higher than before
      if (error < 0 && (closestLowGuess === null || closestLowGuess < guess)) {
        closestLowGuess = guess;
      }

      // On the high side, but lower than before
      if (error > 0 && (closestHighGuess === null || closestHighGuess > guess)) {
        closestHighGuess = guess;
      }

      // Error is technically the $ amount the payback would be wrong, given a certain interest rate
      const errorPercent = error / payback;

      // Divide by the number of months because long-term loans are more sensitve to changes in interest rate
      const errorPerMonth = errorPercent / months;

      if (Math.abs(errorPercent) < allowableError) {
        return 100 * Math.round(guess * precisionMultiplier) / precisionMultiplier;
      }

      // Once we have a range, just use the average as the new guess
      if (closestLowGuess !== null && closestHighGuess !== null) {
        const oldGuess = guess;
        guess = (closestLowGuess + closestHighGuess) / 2;
        const diff = guess - oldGuess;
        continue;
      }

      // If we don't yet have a range, modify our guess based on the error
      const modification = Math.abs(guess) * -errorPerMonth * speedup;
      const newGuess = guess + modification;

      // Not bounded yet, but guess is too low
      if (closestLowGuess !== null && closestHighGuess === null && closestLowGuess > newGuess) {
        guess += .1;
        continue;
      }

      // Not bounded yet, but guess is too high
      if (closestHighGuess !== null && closestLowGuess === null && closestHighGuess < newGuess) {
        guess -= .1;
        continue;
      }

      guess = Math.max(newGuess, allowableError); // Negative or 0 guesses don't make sense
    }

    // Failed to find rate after maxIterations
    return null;
  }

  private cleanOffer(offer: Offer) {
    const cleanedOffer = {};
    // tslint:disable-next-line: forin
    for (const key in offer) {
      const value = offer[key];
      if (value === '' || value === null) {
        continue;
      }
      if (key === 'paymentFrequency') {
        cleanedOffer[key] = value;
      }

      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue === 0) {
        continue;
      }

      if (key === 'apr' && cleanedOffer['interestRate'] === undefined) {
        cleanedOffer['interestRate'] = numValue;
        continue;
      }
      cleanedOffer[key] = numValue;
    }
    return cleanedOffer;
  }

  private monthlyPeriods(frequency: string) {
    switch (frequency) {
      case 'daily': return 21;
      case 'weekly': return 4;
      case 'biweekly': return 2;
      case 'semimonthly': return 2;
      case 'monthly': return 1;
      default: return null;
    }
  }
}
